import { useEffect, useMemo, useState } from "react";
import { ChartLineUp, ArrowsClockwise, DownloadSimple, Broom, Warning } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";
import { isNoTrack, setNoTrack } from "@/lib/analyticsClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function Stat({ label, value, sub, accent }) {
  return (
    <div className={`border ${accent ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/10 bg-[#0F0F0F]"} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">{label}</div>
      <div className={`font-display font-black text-3xl md:text-4xl tracking-tight ${accent ? "text-[#FF1B91]" : "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1">{sub}</div>}
    </div>
  );
}

function ListCard({ title, rows, primary, secondary, empty = "No data yet.", testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#0F0F0F] p-5">
      <h3 className="font-display font-black uppercase text-sm tracking-tight mb-3 text-white/80">{title}</h3>
      {!rows || rows.length === 0 ? (
        <div className="text-white/40 text-xs">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5 last:border-b-0">
              <span className="text-white/85 truncate">{primary(r)}</span>
              <span className="text-[#FF1B91] font-bold whitespace-nowrap">{secondary(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Funnel({ steps }) {
  if (!steps || steps.length === 0) return null;
  const top = steps[0]?.count || 0;
  return (
    <div data-testid="analytics-funnel" className="border border-white/10 bg-[#0F0F0F] p-5">
      <h3 className="font-display font-black uppercase text-sm tracking-tight mb-4 text-white/80">Conversion funnel</h3>
      <div className="space-y-2">
        {steps.map((s, i) => {
          const width = top ? Math.max(6, (100 * s.count) / top) : 6;
          const prev = i > 0 ? steps[i - 1].count : s.count;
          const drop = prev - s.count;
          const dropPct = prev ? Math.round((100 * drop) / prev) : 0;
          return (
            <div key={s.key} data-testid={`funnel-step-${s.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="uppercase tracking-[0.15em] text-white/70 font-bold">{i + 1}. {s.label}</span>
                <span className="text-white/70">
                  <span className="text-white font-bold">{s.count}</span>
                  <span className="text-white/40 ml-2">{s.pct_of_top}%</span>
                  {i > 0 && drop > 0 && (
                    <span className="ml-3 text-[#B32027]">−{drop} ({dropPct}%)</span>
                  )}
                </span>
              </div>
              <div className="h-6 bg-black/40 border border-white/5">
                <div
                  className="h-full bg-gradient-to-r from-[#FF1B91] to-[#B32027]"
                  style={{ width: `${width}%`, transition: "width 400ms ease" }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/40">
        Each row shows how many sessions made it that far and how many dropped off from the prior step.
      </p>
    </div>
  );
}

export default function AnalyticsPanel({ token, defaultOpen = true }) {
  const [range, setRange] = useState("7d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [excluded, setExcluded] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => { setExcluded(isNoTrack()); }, []);
  const toggleExcluded = () => {
    const next = !excluded;
    setNoTrack(next);
    setExcluded(next);
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (useCustom && customStart && customEnd) {
      p.set("start", customStart);
      p.set("end", customEnd);
    }
    p.set("range", range);
    return p.toString();
  }, [range, useCustom, customStart, customEnd]);

  const load = async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const [ov, fn] = await Promise.all([
        fetch(`${BACKEND}/api/admin/analytics/simple?${qs}`, { headers: { "X-Admin-Token": token } }),
        fetch(`${BACKEND}/api/admin/analytics/funnel?${qs}`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (!ov.ok || !fn.ok) throw new Error(`HTTP ${ov.status}/${fn.status}`);
      setData(await ov.json());
      setFunnel(await fn.json());
    } catch (e) {
      setError(e.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [qs, token]);

  const downloadCsv = () => {
    // Token in URL is unavoidable for a direct download link — but the CSV
    // endpoint still requires the admin token to authorize. Use a fetch + Blob.
    fetch(`${BACKEND}/api/admin/analytics/export.csv?${qs}`, { headers: { "X-Admin-Token": token } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `sws-analytics-${range}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const purgeAll = async () => {
    if (!window.confirm("Purge ALL test data — signups, merch orders, AND analytics pageviews/sessions tagged as test? This can't be undone.")) return;
    setPurging(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/purge-all-test-data`, {
        method: "POST", headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      alert(`Purged:\n• ${d.signups_deleted} signups\n• ${d.merch_orders_deleted} merch orders\n• ${d.analytics_deleted} analytics rows\n\nTotal: ${d.total_deleted}`);
      load();
    } finally {
      setPurging(false);
    }
  };

  const t = data?.totals || {};

  return (
    <CollapsibleSection
      id="analytics"
      testId="admin-analytics"
      title={<><ChartLineUp size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Website analytics</>}
      subtitle="Visitors, pageviews, funnel, cities, campaigns."
      onRefresh={load}
      defaultOpen={defaultOpen}
    >
      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            data-testid={`analytics-range-${p.id}`}
            onClick={() => { setRange(p.id); setUseCustom(false); }}
            className={`text-[10px] uppercase tracking-[0.25em] px-3 py-2 border ${!useCustom && range === p.id ? "border-[#FF1B91] bg-[#FF1B91]/10 text-white" : "border-white/15 text-white/60 hover:text-white"}`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-2 border-l border-white/10 pl-3">
          <input data-testid="analytics-custom-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="bg-[#0F0F0F] border border-white/15 text-white text-xs px-2 py-1.5" />
          <span className="text-white/40 text-xs">→</span>
          <input data-testid="analytics-custom-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="bg-[#0F0F0F] border border-white/15 text-white text-xs px-2 py-1.5" />
          <button data-testid="analytics-apply-custom" onClick={() => setUseCustom(true)} disabled={!customStart || !customEnd} className="text-[10px] uppercase tracking-[0.25em] px-3 py-2 border border-[#FF1B91]/60 hover:bg-[#FF1B91]/10 disabled:opacity-40">Apply</button>
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid="analytics-download-csv" onClick={downloadCsv} className="text-[10px] uppercase tracking-[0.25em] px-3 py-2 border border-white/15 text-white/70 hover:text-white inline-flex items-center gap-2">
            <DownloadSimple size={12} weight="bold" /> CSV
          </button>
          <button data-testid="analytics-purge-all" onClick={purgeAll} disabled={purging} className="text-[10px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-[#B32027] text-[#B32027] hover:bg-[#B32027] hover:text-white inline-flex items-center gap-2 font-bold disabled:opacity-50" title="Purge signups + merch orders + analytics test rows">
            <Broom size={12} weight="bold" /> {purging ? "Purging…" : "Purge ALL test data"}
          </button>
          <button data-testid="analytics-refresh" onClick={load} className="text-[10px] uppercase tracking-[0.25em] px-3 py-2 border border-white/15 text-white/60 hover:text-white inline-flex items-center gap-2">
            <ArrowsClockwise size={12} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="border border-[#FF1B91] bg-[#FF1B91]/10 p-3 text-xs mb-4">{error}</div>}

      {/* HUGE "tracking off" banner — the #1 reason "everything says 0" is
          that the admin's own device has no-track on and they're the only
          visitor. Show a bright red warning + a one-click "Track me anyway"
          button right at the top so this is never a silent failure again. */}
      {excluded && (
        <div data-testid="analytics-excluded-banner" className="border-2 border-[#B32027] bg-[#B32027]/15 p-4 md:p-5 mb-6 flex items-center gap-4 flex-wrap">
          <Warning size={28} weight="fill" className="text-[#B32027] flex-shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <p className="font-display font-black uppercase tracking-tight text-lg text-white leading-tight mb-1">
              Your device is excluded from analytics.
            </p>
            <p className="text-white/80 text-sm leading-relaxed">
              That&rsquo;s why numbers look like 0 if you&rsquo;re the only recent visitor.
              Real visitors ARE being counted &mdash; you just can&rsquo;t see yourself.
              Toggle back on if you want to include your own visits.
            </p>
          </div>
          <button
            data-testid="analytics-include-me"
            onClick={toggleExcluded}
            className="border-2 border-white bg-white text-[#B32027] hover:bg-transparent hover:text-white px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] font-bold whitespace-nowrap"
          >
            Track me anyway
          </button>
        </div>
      )}

      {!data && !error && <div className="text-white/50 text-sm py-8 text-center">Loading…</div>}

      {data && (
        <>
          {/* Top-line stats — exactly what the owner asked to see. Signups,
              purchases, and revenue are verified (webhook-confirmed).
              Everything else is available in the funnel + top-pages below. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Visitors" value={t.visitors ?? 0} sub={`${t.sessions ?? 0} sessions`} />
            <Stat label="Pageviews" value={t.pageviews ?? 0} accent />
            <Stat label="Signups" value={t.signups ?? 0} sub="Legacy Challenge" />
            <Stat label="Merch Purchases" value={t.purchases ?? 0} sub="paid + confirmed" />
            <Stat label="Revenue" value={`$${(t.purchase_revenue ?? 0).toFixed(0)}`} accent sub="merch, paid orders" />
          </div>

          {/* Per-page views — this is the "how many people are visiting different
              screens" answer. Given top billing since the owner specifically
              asked for it. */}
          <div className="mb-6">
            <ListCard
              testId="analytics-top-pages"
              title={`Pageviews by screen (${data.top_pages?.length || 0})`}
              rows={data.top_pages}
              primary={(r) => r.path}
              secondary={(r) => `${r.pageviews} views`}
              empty="No pageviews yet in this range."
            />
          </div>

          {/* Funnel — still useful, tucked under the main metrics. */}
          {funnel?.steps && <div className="mb-6"><Funnel steps={funnel.steps} /></div>}

          {/* Secondary metrics — still visible for completeness but less
              prominent so the top-line stays crisp. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Minutes logged" value={t.minutes_total ?? 0} sub={`${t.minutes_logged_events ?? 0} entries`} />
            <Stat label="Wall submissions" value={t.wall_submissions ?? 0} sub={`${t.wall_approved ?? 0} approved`} />
            <Stat label="Donation clicks" value={t.donation_clicks ?? 0} sub="Pledge — unverified" />
            <Stat label="Checkouts started" value={t.merch_checkouts ?? 0} sub="pre-payment" />
          </div>

          <div className="mt-6 border-t border-white/10 pt-4 space-y-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <span className="text-white/60">Definitions:</span>{" "}
              Signups = completed challenge form. ·
              Merch purchases = orders with payment_status = paid, is_test = false. ·
              Revenue = sum of paid, non-test order totals. ·
              Wall submissions = signups that uploaded ≥1 photo.
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">
              Visitors are counted anonymously. Preview + admin traffic auto-excluded.
              Toggle &ldquo;Exclude my device&rdquo; (below) if you want your own visits filtered out.
            </p>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
}
